import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import { LinkField, TextField } from '@sitecore-jss/sitecore-jss-react';
import { classnames } from 'tailwindcss-classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import ArrowDown from 'components/authorable/site/Header/SvgArrowDown/arrow-down';
import Account from 'components/authorable/site/Header/SvgAccount/account';
import Cart from 'components/authorable/site/Header/SvgCart/cart';
import { useScreenBreakpoint } from 'lib/util/useScreenWidth';
import { useSession } from 'next-auth/react';
import { useCartContext } from 'components/globalContext/CartContext';
import { useCart } from 'components/helpers/Cart/CartHelper';
import { appSignOut } from 'lib/auth/client-utils';
import {
  EcommerceDataLayerObject,
  ProductItemRP,
  gtmEcommerceDatalayerPush,
  alwaysSuppressLinkDataLayerPush,
  pageNavigationDataLayerPush,
  pageNavigationDataLayerHandler,
  menuLocation,
} from 'lib/util/gtm-datalayer';

type AccountDropdownItem = {
  id: string;
  displayName: string;
  fields: {
    Action: LinkField;
    Title: TextField;
  };
};

type SignOutProps = {
  tokenID: string;
};

const cssBorderColor = 'border-gray-midGray';
const cssItemsCenter = 'items-center';
const cssTextBlueNavy = 'text-blue-navy';
const cssFontMedium = 'font-medium';
const cssXlBorder = 'xl:border-0';
const cssInlineBlock = 'inline-block';
const ulClassNames = classnames(
  'justify-end',
  'border-t-[24px]',
  'border-gray-whisper',
  cssItemsCenter,
  cssTextBlueNavy,
  cssFontMedium,
  'flex',
  'flex-col',
  cssXlBorder,
  'xl:relative',
  'xl:top-auto',
  'xl:flex-row'
);

const liClassName = classnames(
  'h-full',
  'w-full',
  'border-b',
  cssBorderColor,
  'px-4',
  'py-3',
  cssXlBorder
);

const toggleBtn = classnames(
  'block',
  'w-full',
  'border-b',
  'bg-plum',
  'p-4',
  'py-2.5',
  'text-center',
  'text-white',
  cssFontMedium
);

const hoverFadeIn = classnames(
  'transition',
  'duration-300',
  'ease-in',
  'border-opacity-0',
  'hover:border-opacity-100'
);

const hoverUtilityNavLink = classnames('border-b-2', 'border-green');
const hoverUtilityNav = classnames(hoverFadeIn, hoverUtilityNavLink);
const hoverdropDownLink = classnames('block', 'py-2', 'px-4', hoverFadeIn);

const hoverMobileDownLink = classnames(hoverFadeIn, 'border-b-2', 'xl:border-0');
const dropDownCss = classnames(
  'p-4',
  'border-b',
  cssBorderColor,
  'text-blue',
  'hover:text-blue-navy',
  'xl:border-b-0',
  'xl:p-0',
  'xl:border-t',
  'xl:border-gray-quartz'
);

const cartCss = classnames(
  'absolute',
  'top-[-8px]',
  'left-[8px]',
  'flex',
  'h-[18px]',
  'w-[18px]',
  'justify-center',
  'rounded-full',
  'border',
  'border-white',
  'bg-plum',
  'px-1',
  'text-[9px]',
  'leading-[16px]',
  'text-white'
);

const SignOutBtn = ({ tokenID }: SignOutProps) => {
  const { isLargeScreen } = useScreenBreakpoint(); // >= 1280px exact match
  return (
    <button
      className={classnames(
        'w-full',
        'text-left',
        'text-blue',
        'hover:text-blue-navy',
        cssFontMedium,
        'xl:mb-4',
        'xl:font-regular',
        isLargeScreen ? hoverdropDownLink : null
      )}
      type="button"
      title="Sign out"
      onClick={() => appSignOut(tokenID)}
    >
      <span className={classnames(hoverMobileDownLink, 'border-blue-navy', 'xl:hover:underline')}>
        Sign out
      </span>
    </button>
  );
};

const AccountDropdownLinks = ({ ...profileItem }) => {
  const { data: sessionData } = useSession();
  const token = sessionData?.id_token || '';
  const { isLargeScreen } = useScreenBreakpoint(); // >= 1280px exact match

  return (
    <ul role="menu">
      {profileItem?.fields?.accountDropdownLinks?.map((item: AccountDropdownItem) => {
        const linkId = `nav-links--${item.id}`;
        return (
          <li
            role="menuitem"
            className={classnames(dropDownCss)}
            key={item.id}
            aria-labelledby={linkId}
          >
            <LinkA11yWrapper
              className={classnames(hoverMobileDownLink, {
                [hoverdropDownLink]: isLargeScreen,
                'border-blue-navy': !isLargeScreen,
              })}
              key={item.id}
              editable={false}
              aria-label={item?.fields?.Action?.value?.text}
              field={item?.fields?.Action}
              id={linkId}
              onGtmPush={(e) => {
                pageNavigationDataLayerHandler(e);
              }}
            />
          </li>
        );
      })}
      <li role="menuitem" className={classnames(dropDownCss)} aria-label="Sign Out">
        <SignOutBtn tokenID={token} />
      </li>
    </ul>
  );
};

const Authenticated = ({ ...profileItem }): JSX.Element => {
  const [utilityNavOpen, setUtilityNavOpen] = useState(false);
  const { isLargeScreen } = useScreenBreakpoint(); // >= 1280px exact match
  const router = useRouter();
  const { cartContext } = useCartContext();
  const { data: sessionData } = useSession();
  const token = sessionData?.id_token || '';
  useEffect(() => {
    setUtilityNavOpen(false);
  }, [router?.asPath]);
  const toggleUtilityNav = () => {
    setUtilityNavOpen((prev) => !prev);
    pageNavigationDataLayerPush('Account', menuLocation[0]);
  };
  const fields = profileItem?.profileItem.profileItem;
  const utilityNavRef = useRef<HTMLDivElement | null>(null);
  const closeUtilityNav = useCallback(() => {
    setUtilityNavOpen(false);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (utilityNavRef.current && !utilityNavRef.current.contains(event.target as Node)) {
        closeUtilityNav();
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [closeUtilityNav, utilityNavRef]);

  const { fetchCart } = useCart();
  const handleClickCart = () => {
    const items = fetchCart().map((item, i) => {
      return {
        item_id: item.itemNumber ?? item.id,
        item_name: item.name,
        item_list_name: 'order materials',
        index: i.toString(),
        price: item.price,
        quantity: item.quantity,
      };
    }) as ProductItemRP[];

    const gmtData: EcommerceDataLayerObject = {
      event: 'view_cart',
      ecommerce: {
        value: '0',
        currency: 'USD',
        items: items,
      },
    };
    gtmEcommerceDatalayerPush(gmtData);
  };

  return (
    <>
      <div ref={utilityNavRef}>
        <ul role="menu" className={classnames(ulClassNames)}>
          <li role="menuitem" className={classnames(liClassName)}>
            <div className={classnames('relative')}>
              <div className={classnames(cartCss)}>{cartContext}</div>
              <Cart aria-hidden="true" className={classnames(cssInlineBlock, 'mr-1')} />
              <LinkA11yWrapper
                className={classnames(
                  cssFontMedium,
                  cssTextBlueNavy,
                  hoverFadeIn,
                  hoverUtilityNavLink
                )}
                editable={false}
                field={fields?.cartLink}
                onGtmPush={alwaysSuppressLinkDataLayerPush}
                onClick={handleClickCart}
              />
            </div>
          </li>
          <li role="menuitem" className={classnames(liClassName)}>
            <button
              type="button"
              onClick={toggleUtilityNav}
              className={classnames('flex', cssItemsCenter, 'z-20', 'w-full')}
            >
              <Account
                aria-hidden="true"
                className={classnames(cssInlineBlock, 'mr-1', 'xl:w-auto')}
              />
              <div
                className={classnames(
                  'flex',
                  'h-6',
                  'w-full',
                  'justify-between',
                  'xl:w-fit',
                  'xl:justify-self-start',
                  {
                    [hoverUtilityNav]: isLargeScreen,
                  }
                )}
              >
                <span
                  className={classnames('flex', 'w-fit', cssFontMedium, 'xl:w-fit', {
                    [hoverUtilityNav]: !isLargeScreen,
                  })}
                >
                  Account
                </span>
                <ArrowDown
                  aria-hidden="true"
                  className={classnames(
                    'w-auto',
                    'user-account',
                    'float-right',
                    cssInlineBlock,
                    'ml-1',
                    '-rotate-90',
                    'svg-arrow-down',
                    'xl:float-none',
                    {
                      'xl:rotate-0': !utilityNavOpen,
                      'xl:-rotate-180': utilityNavOpen,
                    }
                  )}
                />
              </div>
            </button>
            {isLargeScreen ? (
              <div
                className={classnames('user-account-dropdown', 'min-w-[263px]', 'p-2', 'right-0', {
                  'xl:hidden': !utilityNavOpen,
                  'xl:block': utilityNavOpen,
                  hidden: utilityNavOpen || !utilityNavOpen,
                })}
              >
                <div className={classnames('py-2', cssFontMedium)}>
                  {fields?.accountLinkText?.value as string}
                  {sessionData?.user?.name?.split(' ')[0]}
                </div>
                <AccountDropdownLinks fields={fields} tokenID={token} />
              </div>
            ) : (
              <div
                className={classnames(
                  'absolute',
                  'top-0',
                  'bg-white',
                  'h-full',
                  'transition-transform',
                  'w-full',
                  'z-30',
                  'xl:hidden',
                  {
                    '-translate-x-full': !utilityNavOpen,
                    'translate-x-0': utilityNavOpen,
                    'left-0': utilityNavOpen,
                    'left-[-100%]': !utilityNavOpen,
                  }
                )}
              >
                <button type="button" onClick={toggleUtilityNav} className={classnames(toggleBtn)}>
                  <div>
                    <ArrowDown
                      aria-hidden="true"
                      className={classnames(
                        'svg-arrow-down',
                        'float-left',
                        '-ml-2',
                        cssInlineBlock,
                        'rotate-90'
                      )}
                    />
                    Account
                  </div>
                </button>
                <div
                  className={classnames('px-4', 'py-2', 'border-b', cssBorderColor, cssFontMedium)}
                >
                  {fields?.accountLinkText?.value as string}
                  {sessionData?.user?.name?.split(' ')[0]}
                </div>
                <AccountDropdownLinks fields={fields} tokenID={token} />
              </div>
            )}
          </li>
          {!isLargeScreen && (
            <>
              <li
                role="menuitem"
                aria-label="Sign Out"
                className={classnames(
                  'h-full',
                  'w-full',
                  'border-b',
                  'p-4',
                  cssBorderColor,
                  cssXlBorder
                )}
              >
                <SignOutBtn tokenID={token} />
              </li>
            </>
          )}
        </ul>
      </div>
    </>
  );
};

export default Authenticated;
